import { useState } from 'react';
import { X, Layers, RefreshCw } from 'lucide-react';
import type { Story, StoryPage } from '@/types/story';
import PageRegenerationModal from './PageRegenerationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url: string | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

interface StoryboardEditorProps {
  story: Story;
  onClose: () => void;
  onPageUpdated: (pageNumber: number, newImageUrl: string) => void;
}

const REGENERATIONS_LIMIT = 2;

const StoryboardEditor: React.FC<StoryboardEditorProps> = ({
  story,
  onClose,
  onPageUpdated,
}) => {
  const [selectedPage, setSelectedPage] = useState<StoryPage | null>(null);
  const [regeneratingPage, setRegeneratingPage] = useState<number | null>(null);
  const [regenerationCount, setRegenerationCount] = useState<number>(story.regeneration_count || 0);

  const regenerationsRemaining = REGENERATIONS_LIMIT - regenerationCount;

  const handleRegenerateClick = (page: StoryPage) => {
    setSelectedPage(page);
  };

  const handleModalClose = () => {
    setSelectedPage(null);
  };

  const handleRegenerationSuccess = (pageNumber: number, newImageUrl: string) => {
    onPageUpdated(pageNumber, newImageUrl);
    setRegenerationCount(prev => prev + 1); // Increment local count
    setSelectedPage(null);
  };

  const handleRegenerationStart = (pageNumber: number) => {
    setRegeneratingPage(pageNumber);
  };

  const handleRegenerationEnd = () => {
    setRegeneratingPage(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: 'var(--st-bg-page)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4 border-b"
        style={{ borderColor: 'var(--st-border-light)', background: 'var(--st-bg-surface)' }}
      >
        <div className="flex items-center gap-3">
          <Layers className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
          <h1
            className="text-xl font-bold"
            style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
          >
            Storyboard Editor
          </h1>
          <span
            className="text-sm"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            {story.pages.length} pages
          </span>
          <span
            className={`text-sm px-2 py-0.5 rounded-full ${
              regenerationsRemaining > 0
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
            }`}
            style={{ fontFamily: 'Quicksand, sans-serif' }}
          >
            {regenerationsRemaining} regeneration{regenerationsRemaining !== 1 ? 's' : ''} left
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-orange-50 dark:hover:bg-white/10 transition-colors"
          title="Close storyboard"
        >
          <X className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
        </button>
      </div>

      {/* Grid of pages */}
      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {story.pages.map((page) => (
            <div
              key={page.page_number}
              className="group relative rounded-xl overflow-hidden transition-all hover:scale-[1.02]"
              style={{
                background: 'var(--st-bg-surface)',
                border: '1px solid var(--st-border-light)',
                boxShadow: '0 8px 24px rgba(var(--st-shadow-color), 0.12)',
              }}
            >
              {/* Image */}
              <div className="aspect-[16/9] relative overflow-hidden">
                {page.image_url ? (
                  <img
                    src={getImageUrl(page.image_url) || ''}
                    alt={`Page ${page.page_number}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: 'var(--st-border-light)' }}
                  >
                    <span
                      className="text-sm"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                    >
                      No image
                    </span>
                  </div>
                )}

                {/* Page number badge */}
                <div
                  className="absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-semibold bg-white/90 dark:bg-black/70"
                  style={{
                    color: 'var(--st-text-heading)',
                    fontFamily: 'Quicksand, sans-serif',
                  }}
                >
                  Page {page.page_number}
                </div>

                {/* Loading overlay */}
                {regeneratingPage === page.page_number && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <RefreshCw className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}

                {/* Hover overlay with regenerate button */}
                {regeneratingPage !== page.page_number && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => handleRegenerateClick(page)}
                      className="px-4 py-2 rounded-xl font-semibold text-sm transition-all hover:scale-105"
                      style={{
                        background: 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-primary-hover) 100%)',
                        color: 'var(--st-text-on-primary)',
                        fontFamily: 'Quicksand, sans-serif',
                        boxShadow: '0 6px 20px rgba(var(--st-glow-color), 0.4)',
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Regenerate
                      </span>
                    </button>
                  </div>
                )}
              </div>

              {/* Text preview */}
              <div className="p-3">
                <p
                  className="text-xs line-clamp-2"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: 'var(--st-text-body)',
                  }}
                >
                  {page.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Regeneration Modal */}
      {selectedPage && (
        <PageRegenerationModal
          page={selectedPage}
          story={story}
          isOpen={true}
          onClose={handleModalClose}
          onSuccess={handleRegenerationSuccess}
          onStart={handleRegenerationStart}
          onEnd={handleRegenerationEnd}
        />
      )}
    </div>
  );
};

export default StoryboardEditor;
